import { Component, inject, Input, OnChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { PermissionService } from '../../../../core/services/permission.service';
import { FinancialReconciliationSummary } from '../../../financial-reconciliation/models/financial-reconciliation.model';
import { DashboardActions, DashboardChannel } from '../../models/dashboard.model';
import { HOVER_CAPABLE } from '../../../../shared/utils/device';
import { ActionItem, ActionPendings, buildActionItems } from '../../utils/dashboard-actions';

// "Pede ação agora": o que precisa de uma decisão, em itens com número, frase e link para a lista
// já filtrada, numa lista compacta (uma linha por item; os primeiros 4, o resto atrás de "Ver mais").
// Substitui os banners de alerta. Sem `actions` (API antiga) não renderiza nada; com
// `actions` e sem item, diz que nada pede ação. Cada item respeita a permissão de quem vê.
@Component({
  selector: 'app-dashboard-actions',
  standalone: true,
  imports: [RouterModule, NgbTooltipModule],
  templateUrl: './dashboard-actions.component.html',
  styleUrl: './dashboard-actions.component.scss',
  host: { class: 'd-block' },
})
export class DashboardActionsComponent implements OnChanges {
  @Input() actions?: DashboardActions | null;
  /** integrationId do filtro de canal; null = todos. */
  @Input() channelId: number | null = null;
  @Input() channels?: DashboardChannel[] | null;
  /** Fila de pendências da empresa (`api/pendings`), já resumida. */
  @Input() pendings?: ActionPendings | null;
  /** Resumo da conciliação do período, base do item de margem baixa. */
  @Input() margin?: FinancialReconciliationSummary | null;
  /** Faturamento do período, para a regra de margem indisponível. */
  @Input() revenue?: number | null;
  @Input() payoutPartial?: number | null;
  /** Período (yyyy-MM-dd) dos itens que são do período: margem e repasse parcial. */
  @Input() period?: { from?: string; to?: string };

  /** Quantos itens aparecem antes do "Ver mais". */
  readonly limit = 4;
  expanded = false;
  // Toque não tem hover: tooltip em link atrapalha o toque no iOS (ver utils/device.ts).
  readonly hoverCapable = HOVER_CAPABLE;

  private readonly permissions = inject(PermissionService);
  private all: ActionItem[] = [];

  // Itens montados uma vez por mudança de entrada: referência estável pros links do router.
  ngOnChanges(): void {
    this.all = buildActionItems({
      actions: this.actions,
      channelId: this.channelId,
      channels: this.channels,
      pendings: this.pendings,
      margin: this.margin,
      revenue: this.revenue,
      payoutPartial: this.payoutPartial,
      period: this.period,
    });
  }

  get ready(): boolean {
    return !!this.actions;
  }

  get items(): ActionItem[] {
    return this.all.filter((i) => this.permissions.hasAny(i.roles));
  }

  visible(list: ActionItem[]): ActionItem[] {
    return this.expanded ? list : list.slice(0, this.limit);
  }

  /** Frase inteira do item: o rótulo da linha pode terminar em reticências. */
  tip(i: ActionItem): string {
    const extra = i.hint ?? i.detail;
    return `${i.value} ${i.short ?? i.text}${extra ? `. ${extra}` : ''}`;
  }
}
